// NexSys Command Center — API layer.
// Talks to the FastAPI backend (sample data today; swap for real integrations later:
// OpenAI / Azure OpenAI / Jira / Azure DevOps / ServiceNow / SAP / Power BI ...).
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeaders = () => {
  const t = localStorage.getItem("nexsys_gate_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

async function getJSON(path) {
  const r = await fetch(`${API}${path}`, { headers: { ...authHeaders() } });
  if (!r.ok) throw new Error(`GET ${path} -> ${r.status}`);
  return r.json();
}

async function postJSON(path, body) {
  const r = await fetch(`${API}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(`POST ${path} -> ${r.status}`);
  return r.json();
}

async function patchJSON(path, body) {
  const r = await fetch(`${API}${path}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(`PATCH ${path} -> ${r.status}`);
  return r.json();
}

async function postJSONDetailed(path, body) {
  const r = await fetch(`${API}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(body),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) {
    const d = data?.detail;
    throw { detail: typeof d === "string" ? d : JSON.stringify(d || `POST ${path} -> ${r.status}`) };
  }
  return data;
}

export const nexsysApi = {
  portfolio: () => getJSON("/nexsys/portfolio"),
  programs: () => getJSON("/nexsys/programs"),
  milestones: () => getJSON("/nexsys/milestones"),
  insights: () => getJSON("/nexsys/insights"),
  predictive: () => getJSON("/nexsys/predictive"),
  executions: (state = "") => getJSON(`/nexsys/executions${state ? `?state=${state}` : ""}`),
  executionDetail: (id) => getJSON(`/nexsys/executions/${id}`),
  executionTransition: async (id, action, reason = "") => {
    const r = await fetch(`${API}/nexsys/executions/${id}/transition`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify({ action, reason }),
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) { const e = new Error(`transition -> ${r.status}`); e.body = j; throw e; }
    return j;
  },
  authorizeExecution: async (id) => {
    const r = await fetch(`${API}/nexsys/decisions/${id}/authorize-execution`, {
      method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() },
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) { const e = new Error(`authorize-execution -> ${r.status}`); e.status = r.status; e.body = j; throw e; }
    return j;
  },
  executeDecision: async (id, body) => {
    const r = await fetch(`${API}/nexsys/decisions/${id}/execute`, {
      method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify(body),
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) { const e = new Error(`execute -> ${r.status}`); e.status = r.status; e.body = j; throw e; }
    return j;
  },
  timeseries: () => getJSON("/nexsys/timeseries"),
  analyze: (command) => postJSON("/nexsys/analyze", { command }),
  assistant: (message, context = "") => postJSON("/nexsys/assistant", { message, context }),
  decisions: () => getJSON("/nexsys/decisions"),
  approveDecision: (body) => postJSON("/nexsys/decisions/approve", body),
  createExecutionDecision: (body) => postJSONDetailed("/nexsys/decisions/execution-decisions", body),
  attachExecutionEffect: (id, body) => postJSONDetailed(`/nexsys/decisions/${id}/execution-effect`, body),
  simulateDecision: (body) => postJSON("/nexsys/decisions/simulate", body),
  analyzeDecision: (body) => postJSON("/nexsys/decisions/analysis", body),
  integrations: () => getJSON("/nexsys/integrations"),
  hosting: () => getJSON("/nexsys/system/hosting"),
  requestIntegration: (body) => postJSON("/nexsys/integrations/request", body),
  updateIntegrationStatus: (connector_id, status) => patchJSON(`/nexsys/integrations/${connector_id}/status`, { status }),
  cognitive: () => getJSON("/nexsys/cognitive"),
  orchestrate: (body) => postJSON("/nexsys/orchestrate", body),
  memory: (limit = 12) => getJSON(`/nexsys/memory?limit=${limit}`),
  getDecision: (id) => getJSON(`/nexsys/memory/${id}`),
  recall: (q, limit = 3) => getJSON(`/nexsys/memory/recall?q=${encodeURIComponent(q)}&limit=${limit}`),
  recordOutcome: (id, body) => patchJSON(`/nexsys/memory/${id}`, body),
  continuumMe: () => getJSON("/nexsys/continuum/me"),
  continuumResume: () => postJSON("/nexsys/continuum/resume", {}),
  continuumConfig: () => getJSON("/nexsys/continuum/config"),
  continuumHold: (id, legal_hold) => patchJSON(`/nexsys/continuum/hold/${id}`, { legal_hold }),
  continuumExport: () => getJSON("/nexsys/continuum/export"),
  gatewayAudit: () => getJSON("/gateway/audit?limit=50"),
  gatewayInterfaces: () => getJSON("/gateway/interfaces"),
  gatewayInterfaceCreate: (name, provider) => postJSON("/gateway/interfaces", { name, provider }),
  gatewayInterfaceStatus: (id, status) => patchJSON(`/gateway/interfaces/${id}`, { status }),
  gatewayBindings: () => getJSON("/gateway/bindings"),
  gatewayResolveConflict: (id, action, reason, reviewed_state_version) =>
    postJSON(`/gateway/conflicts/${id}/resolve`, { action, reason, reviewed_state_version }),
  gatewayEscalations: () => getJSON("/gateway/escalations"),
  gatewayEscalationAssign: (id, assignee) => postJSON(`/gateway/escalations/${id}/assign`, { assignee }),
  gatewayEscalationStatus: (id, status, note = "") => postJSON(`/gateway/escalations/${id}/status`, { status, note }),
  gatewayEscalationPriority: (id, priority, reason = "") => postJSON(`/gateway/escalations/${id}/priority`, { priority, reason }),
  oauthGrants: () => getJSON("/oauth/grants"),
  oauthGrantRevoke: (id) => postJSON(`/oauth/grants/${id}/revoke`, {}),
  myBindings: () => getJSON("/gateway/bindings/me"),
  createMyBinding: (external_identity, provider) => postJSON("/gateway/bindings/me", { external_identity, provider }),
  revokeMyBinding: (id) => postJSON(`/gateway/bindings/me/${id}/revoke`, {}),
  conflictsSummary: () => getJSON("/gateway/conflicts/summary"),
  conflictsSeen: () => postJSON("/gateway/conflicts/seen", {}),
  governanceStatus: () => getJSON("/nexsys/governance/status"),
  vantaPosture: () => getJSON("/integrations/vanta/posture"),
  vantaSignals: () => getJSON("/integrations/vanta/signals"),
  vantaSubmitEligible: () => postJSON("/integrations/vanta/evidence/submit-eligible", {}),
};
